import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { format, formatDistanceToNow } from "date-fns"
import {
	IconKeyOutline18 as KeyRound,
	IconPlusOutline18 as Plus,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"

import {
	AlertDialog,
	Avatar,
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	Tooltip,
} from "@productlane/ui"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { SettingsSection } from "../../../settings-switches/settings-section"
import { CreateApiKeyDialog } from "./create-api-key-dialog"

export function ApiKeysBlock() {
	const [keys] = useQuery(queries.apiKeysV2())
	const [isCreateOpen, setIsCreateOpen] = useState(false)

	const activeKeys = useMemo(() => keys.filter((k) => !k.revokedAt), [keys])

	return (
		<>
			<SettingsSection
				title="API keys"
				description="Use API keys to authenticate with the public API. Each key has a set of scopes that limit what it can do."
			/>
			<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
				{activeKeys.length === 0 ? (
					<div className="flex items-center justify-between px-4 pb-4">
						<div className="text-sm text-secondary">No API keys created</div>
						<Button
							variant="ghost"
							Icon={Plus}
							onClick={() => setIsCreateOpen(true)}
							className="text-primary"
						>
							New API key
						</Button>
					</div>
				) : (
					<div className="flex flex-col">
						<div className="mb-2 flex items-center justify-between border-b px-4 pb-4">
							<div className="text-sm font-medium">
								{activeKeys.length}{" "}
								{activeKeys.length === 1 ? "API key" : "API keys"}
							</div>
							<Button
								variant="ghost"
								Icon={Plus}
								className="text-primary"
								onClick={() => setIsCreateOpen(true)}
							>
								New API key
							</Button>
						</div>
						{activeKeys.map((key) => (
							<ApiKeyRow key={key.id} apiKey={key} />
						))}
					</div>
				)}
			</SettingsContainer>
			<CreateApiKeyDialog
				isOpen={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
			/>
		</>
	)
}

interface ApiKeyV2Row {
	id: string
	label: string
	keyPreview: string
	scopes: readonly string[]
	createdAt: number
	lastUsedAt: number | null
	createdBy?: {
		id: string
		name?: string | null
		email: string
		imageUrl?: string | null
	}
}

// Two members can share a name, so the email always says which one made the key.
export function creatorLabel(
	createdBy: ApiKeyV2Row["createdBy"] | undefined,
): string {
	if (!createdBy) return "—"
	return createdBy.name
		? `${createdBy.name} (${createdBy.email})`
		: createdBy.email
}

function ApiKeyRow({ apiKey }: { apiKey: ApiKeyV2Row }) {
	const ctx = api.useUtils()
	const [confirmRevoke, setConfirmRevoke] = useState(false)

	const revokeMutation = api.workspace.revokeApiKeyV2.useMutation({
		onSuccess: () => {
			toast.success({ title: "API key revoked" })
			void ctx.workspace.invalidate()
		},
	})

	const isFullAccess = apiKey.scopes.includes("admin")
	const creatorName = creatorLabel(apiKey.createdBy)
	const createdAgo = formatDistanceToNow(new Date(apiKey.createdAt), {
		addSuffix: true,
	})
	const lastUsed = apiKey.lastUsedAt
		? `last used on ${format(new Date(apiKey.lastUsedAt), "MMM d, yyyy")}`
		: "never used"

	return (
		<>
			<div className="flex items-center gap-3 border-b px-4 py-4 last:border-b-0">
				<div className="relative">
					<Tooltip label={`Created by ${creatorName}`}>
						<Avatar
							name={apiKey.createdBy?.name ?? apiKey.createdBy?.email ?? "?"}
							image={apiKey.createdBy?.imageUrl}
							roundness="full"
							size="sm"
						/>
					</Tooltip>
					<div className="absolute -bottom-0 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700/90">
						<KeyRound className="h-3 w-3" />
					</div>
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-x-2 text-sm">
						<span className="truncate font-medium">{apiKey.label}</span>
						<span className="text-secondary">·</span>
						<Tooltip
							label={
								isFullAccess
									? "Admin scope - can do everything"
									: apiKey.scopes.join(", ")
							}
						>
							<span className="select-none text-secondary underline decoration-dotted underline-offset-2">
								{isFullAccess
									? "full access"
									: `${apiKey.scopes.length} ${apiKey.scopes.length === 1 ? "scope" : "scopes"}`}
							</span>
						</Tooltip>
						<span className="text-secondary">·</span>
						<span className="font-mono text-secondary">
							{apiKey.keyPreview}…
						</span>
					</div>
					<div className="truncate text-xs text-secondary">
						{creatorName} created {createdAgo} · {lastUsed}
					</div>
				</div>
				<DropdownMenu
					trigger={<IconButton Icon={MoreHorizontal} variant="ghost" />}
				>
					<DropdownMenuItem
						onClick={() => setConfirmRevoke(true)}
						Icon={Trash2}
					>
						Revoke
					</DropdownMenuItem>
				</DropdownMenu>
			</div>
			<AlertDialog
				isOpen={confirmRevoke}
				onOpenChange={setConfirmRevoke}
				title="Revoke API key?"
				description={`The key "${apiKey.label}" will stop working immediately. Any integrations using it will break.`}
				confirmText="Revoke"
				onCancel={() => setConfirmRevoke(false)}
				onConfirm={() => {
					revokeMutation.mutate({ id: apiKey.id })
					setConfirmRevoke(false)
				}}
			/>
		</>
	)
}
